import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { HireChefDialog } from "@/components/HireChefDialog";
import api, { fileUrl } from "@/lib/api";
import { Star, ShieldCheck, ChefHat } from "lucide-react";

export default function ChefPortfolio() {
  const { chefId } = useParams();
  const [chef, setChef] = useState(null);
  const [err, setErr] = useState(null);
  const [hireOpen, setHireOpen] = useState(false);

  useEffect(() => {
    api.get(`/chefs/${chefId}`).then((r) => setChef(r.data)).catch(() => setErr("Chef not found"));
  }, [chefId]);

  if (err) return (<><Navbar /><div className="max-w-3xl mx-auto px-6 py-20 tick-label">{err}</div></>);
  if (!chef) return (<><Navbar /><div className="max-w-3xl mx-auto px-6 py-20 tick-label">Loading...</div></>);

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-14">
        <div className="grid md:grid-cols-3 gap-10">
          <div className="md:col-span-1">
            <div className="aspect-square bg-neutral-900 border hairline overflow-hidden">
              {chef.avatar_url ? (
                <img src={fileUrl(chef.avatar_url)} alt={chef.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ChefHat size={64} className="text-neutral-700" />
                </div>
              )}
            </div>
            {chef.license_verified && (
              <div className="mt-3 tick-label flex items-center gap-1.5 text-[#FF3B30]">
                <ShieldCheck size={13} /> Licensed
              </div>
            )}
            <div className="mt-6 grid grid-cols-3 gap-0 border hairline">
              <Stat label="Rating" value={chef.rating > 0 ? chef.rating.toFixed(1) : "—"} />
              <Stat label="Reviews" value={chef.review_count} />
              <Stat label="Bookings" value={chef.paid_bookings} />
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="tick-label">Chef</div>
            <h1 data-testid="chef-name" className="font-display font-black text-5xl lg:text-6xl tracking-tighter mt-1">{chef.name}</h1>

            {chef.bio && (
              <p className="mt-6 text-neutral-300 leading-relaxed whitespace-pre-wrap">{chef.bio}</p>
            )}

            {chef.specialties?.length > 0 && (
              <div className="mt-8">
                <div className="tick-label">Specialties</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {chef.specialties.map((s, i) => (
                    <span key={i} className="border hairline px-3 py-1.5 text-sm">{s}</span>
                  ))}
                </div>
              </div>
            )}

            {chef.portfolio_photos?.length > 0 && (
              <div className="mt-10">
                <div className="tick-label mb-4">Portfolio</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {chef.portfolio_photos.map((p, i) => (
                    <a key={i} href={fileUrl(p)} target="_blank" rel="noreferrer" className="aspect-square bg-neutral-900 border hairline overflow-hidden block">
                      <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" loading="lazy" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {chef.reviews?.length > 0 && (
              <div className="mt-10">
                <div className="tick-label mb-4">What hosts & clients say</div>
                <div className="space-y-5">
                  {chef.reviews.map((r) => (
                    <div key={r.id} className="border hairline p-5">
                      <div className="flex items-center gap-2">
                        {[1,2,3,4,5].map(i => (
                          <Star key={i} size={13} className={i<=r.rating?"fill-[#FF3B30] text-[#FF3B30]":"text-neutral-700"} />
                        ))}
                      </div>
                      {r.comment && <p className="mt-3 text-neutral-300 text-sm">{r.comment}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-10 border-t hairline pt-6 flex flex-wrap gap-3">
              <button data-testid="hire-chef-btn" onClick={() => setHireOpen(true)} className="btn-signal">
                Hire {chef.name.split(" ")[0]}
              </button>
              <Link to="/browse" className="btn-outline">Book a kitchen</Link>
            </div>
          </div>
        </div>
        <HireChefDialog chef={chef} open={hireOpen} onClose={() => setHireOpen(false)} />
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="p-3 border-r hairline last:border-r-0">
      <div className="tick-label">{label}</div>
      <div className="font-mono text-lg font-bold mt-1">{value}</div>
    </div>
  );
}
